import apiClient from './apiClient';

export interface Payment {
  id: string;
  userId: string;
  userName: string;
  userEmail: string;
  amount: number;
  currency: string;
  status: 'succeeded' | 'failed' | 'pending';
  createdAt: string;
  invoiceUrl?: string;
}

export interface PaymentsResponse {
  data: Payment[];
  total: number;
  page: number;
  totalPages: number;
  limit: number;
}

export interface PaymentFilters {
  page?: number;
  limit?: number;
  status?: string;
  startDate?: string;
  endDate?: string;
}

export const billingService = {
  /**
   * Get admin payment history with filtering and pagination
   * @param filters - Filter criteria (status, date range, pagination)
   * @returns Paginated payment history
   */
  getPayments: async (filters: PaymentFilters): Promise<PaymentsResponse> => {
    try {
      const params = new URLSearchParams();
      
      if (filters.page) params.append('page', filters.page.toString());
      if (filters.limit) params.append('limit', filters.limit.toString());
      if (filters.status) params.append('status', filters.status);
      if (filters.startDate) params.append('startDate', filters.startDate);
      if (filters.endDate) params.append('endDate', filters.endDate);

      const response = await apiClient.get<PaymentsResponse>(
        `/billing/admin/payments?${params.toString()}`
      );
      
      return response.data;
    } catch (error) {
      console.error('Error fetching payments:', error);
      throw error;
    }
  },

  /**
   * Export payment history to CSV
   * @param filters - Filter criteria for export
   * @returns CSV data as string
   */
  exportPaymentsToCSV: async (filters: PaymentFilters): Promise<string> => {
    try {
      // Fetch all filtered data (without pagination limit)
      const response = await billingService.getPayments({
        ...filters,
        page: 1,
        limit: 1000, // Get up to 1000 records
      });

      // Generate CSV headers
      const headers = ['Payment ID', 'Customer Name', 'Customer Email', 'Amount', 'Status', 'Date'];
      const csvContent = [
        headers.join(','),
        ...response.data.map(payment => [
          `"${payment.id}"`,
          `"${payment.userName}"`,
          `"${payment.userEmail}"`,
          (payment.amount / 100).toFixed(2),
          payment.status,
          new Date(payment.createdAt).toLocaleDateString('en-US'),
        ].join(',')),
      ].join('\n');

      return csvContent;
    } catch (error) {
      console.error('Error exporting payments:', error);
      throw error;
    }
  },
};
